import { FunctionComponent, JSX, useEffect, useState } from 'react'
import { ChildDaycareWithParents, ChildParent } from '@/types/Child.ts'
import styles from './taskAndNotesChild.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ArrowForward, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useLazyQuery } from '@apollo/client'
import { GET_TASK_NOTES_FOR_PROFILE } from '@/modules/API/taskNote/getTaskNotes.ts'
import { TaskNote, TaskNoteProfile } from '@/types/TaskNote.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import AddTasksNotesDialog from '../../../taskNote/Dialog/addTasksNotesDialog.tsx'
import TaskNoteComponent from '../../../taskNote/taskNoteComponent/taskNoteComponent.tsx'
import { Parent } from '@/types/Parent.ts'

interface TaskAndNotesChildProps {
    daycare: ChildDaycareWithParents
    childId: number
    parents: ChildParent[]
    childName: string
    showRegular: boolean
    canUpdate: boolean
    handleRefetchChild: () => void
}

const TaskAndNotesChild: FunctionComponent<TaskAndNotesChildProps> = ({
    daycare,
    childId,
    parents,
    childName,
    showRegular,
    canUpdate,
    handleRefetchChild
}: TaskAndNotesChildProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [tasksAndNotesChilds, setTasksAndNotesChilds] = useState<TaskNoteProfile>()
    const [otherTasksAndNotesCount, setOtherTasksAndNotesCount] = useState<number>(0)

    const [showMore, setShowMore] = useState<boolean>(!showRegular)

    const [addTasksNotesDialogOpen, setAddTasksNotesDialogOpen] = useState<boolean>(false)

    const [getTaskNotesForProfile, { loading: loadingTaskNotes, data: dataTaskNotes }] = useLazyQuery(
        GET_TASK_NOTES_FOR_PROFILE,
        {
            variables: {
                daycareId: daycare.daycareId,
                childId
            }
        }
    )

    useEffect(() => {
        if (dataTaskNotes && dataTaskNotes.getTaskNotesForProfile) {
            setTasksAndNotesChilds(dataTaskNotes.getTaskNotesForProfile)
            if (dataTaskNotes.getTaskNotesForProfile.count > 2) {
                setOtherTasksAndNotesCount(dataTaskNotes.getTaskNotesForProfile.count - 2)
            }
        }
    }, [dataTaskNotes])

    useEffect(() => {
        getTaskNotesForProfile()
    }, [daycare])

    return (
        <div className={styles.Form}>
            <div className={`${styles.TitleAndButton} ${!showMore ? styles.NoMarginBottom : ''}`}>
                <div className={styles.Title} onClick={() => setShowMore(!showMore)}>
                    <Title text={t('tasksAndNotes')}></Title>
                    {showMore ? (
                        <KeyboardArrowUp className={styles.Icon} />
                    ) : (
                        <KeyboardArrowDown className={styles.Icon} />
                    )}
                </div>
                {canUpdate && showMore && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'add'}
                            onClick={() => setAddTasksNotesDialogOpen(true)}
                            className={styles.Button}
                        />
                    </div>
                )}
            </div>
            {loadingTaskNotes && <Loading overall={false} />}
            {showMore && (
                <>
                    {tasksAndNotesChilds && tasksAndNotesChilds.count > 0 ? (
                        <div className={styles.TaskNoteContainer}>
                            {tasksAndNotesChilds.taskNotes.map(
                                (taskNote: TaskNote): JSX.Element => (
                                    <div
                                        className={
                                            tasksAndNotesChilds.taskNotes.length >= 2
                                                ? styles.TaskNote
                                                : styles.TaskNoteFullWidth
                                        }
                                        key={taskNote.id}
                                    >
                                        <TaskNoteComponent
                                            taskNote={taskNote}
                                            isParent={false}
                                            isRpeOrLaep={daycare.isRpeOrLaep}
                                            canUpdate={canUpdate}
                                            daycareId={daycare.daycareId}
                                            handleRefetch={handleRefetchChild}
                                            parents={convertChildParentsToParent(parents)}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className={styles.NoTasksNotes}>{t('noTasksAndNotes')}</div>
                    )}
                    {tasksAndNotesChilds && otherTasksAndNotesCount > 0 && (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                customText={
                                    otherTasksAndNotesCount +
                                    ' ' +
                                    `${otherTasksAndNotesCount === 1 ? t('other').toLowerCase() : t('others').toLowerCase()}` +
                                    ' ' +
                                    `${t('tasksAndNotes').toLowerCase()}`
                                }
                                customIcon={<ArrowForward />}
                                onClick={() => navigate('/taskNote?childId=' + childId)}
                            />
                        </div>
                    )}
                </>
            )}
            {addTasksNotesDialogOpen && canUpdate && (
                <AddTasksNotesDialog
                    open={addTasksNotesDialogOpen}
                    handleClose={() => setAddTasksNotesDialogOpen(false)}
                    childId={childId}
                    daycareId={daycare.daycareId}
                    childName={childName}
                    parents={convertChildParentsToParent(parents)}
                    isRpeOrLaep={daycare.isRpeOrLaep}
                    handleRefetch={handleRefetchChild}
                />
            )}
        </div>
    )
}

export default TaskAndNotesChild

function convertChildParentsToParent(parents: ChildParent[]): Parent[] {
    return parents.map((parent: ChildParent): Parent => {
        return {
            address: '',
            addressSecond: '',
            childs: [],
            city: '',
            citySecond: '',
            country: '',
            countrySecond: '',
            created: new Date(),
            daycareRegistered: false,
            daycares: [],
            email: parent.email,
            enrolment: '',
            firstLogin: false,
            firstname: parent.firstname,
            id: parent.parentId,
            isNanny: parent.isNanny,
            lastname: parent.lastname,
            notifAvailabilityMail: false,
            notifAvailabilityPush: false,
            notifBookingMail: false,
            notifBookingPush: false,
            notifEventMail: false,
            notifEventPush: false,
            notifNewPostPush: false,
            notifReminderMail: false,
            notifReminderPush: false,
            notifTaskNote: false,
            notifTaskNotePush: false,
            phone: parent.phone,
            phoneSecond: '',
            updated: new Date(),
            zip: '',
            zipSecond: ''
        }
    })
}
